import { use, useEffect, useState } from 'react';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';

import { useGatewaysContextProvider } from 'features/gateway/context/gateway-provider';
import Gateway from 'features/gateway/domain/models/gateway';
import { useGenerateSecrets } from 'features/gateway/domain/queries/gateway-queries';

export interface IGatewaySettingsIoTEdgeClusterClientHook {
  deviceId: string | undefined;
  ioTEdgeConnectionString: string;
  generateIoTEdgeConnectionStringIsLoading: boolean;
  isGenerateConfirmationOpen: boolean;
  defaultIoTEdgeConnectionString: string;

  generateIoTEdgeClusterDeviceConnectionString: () => void;
  submitGenerateIoTEdgeClusterDeviceConnectionString: () => void;
  closeGenerateConfirmationPopup: () => void;
}

const useGatewaySettingsIoTEdgeClusterClient = (childGateway: Gateway | undefined): IGatewaySettingsIoTEdgeClusterClientHook => {
  const { setErrorMessage } = use(ErrorMessageProviderContext);

  const { gatewayDetailsHook } = useGatewaysContextProvider();

  const defaultIoTEdgeConnectionString = '********';

  const [isGenerateConfirmationOpen, setIsGenerateConfirmationOpen] = useState(false);
  const [deviceId, setDeviceId] = useState(childGateway?.deviceId);
  const [ioTEdgeConnectionString, setIoTEdgeConnectionString] = useState(defaultIoTEdgeConnectionString);

  const {
    mutate: generateSecrets,
    isPending: generateSecretIsPending,
    isError: generateSecretIsError,
    error: generateSecretError,
    isSuccess: generateSecretIsSuccess,
    data: generateSecretData,
  } = useGenerateSecrets();

  useEffect(() => {
    if (generateSecretIsError && generateSecretError) {
      setErrorMessage(generateSecretError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateSecretIsError]);

  useEffect(() => {
    if (generateSecretData) {
      setDeviceId(generateSecretData.deviceId);
      setIoTEdgeConnectionString(generateSecretData.ioTEdgeConnectionString ?? defaultIoTEdgeConnectionString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateSecretIsSuccess]);

  useEffect(() => {
    setDeviceId(childGateway?.deviceId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gatewayDetailsHook.currentSelectedGateway]);

  const generateIoTEdgeConnectionString = () => {
    if (deviceId && deviceId.length > 0) {
      setIsGenerateConfirmationOpen(true);
    } else {
      const gatewayId = childGateway?.id;
      if (gatewayId) {
        generateSecrets(gatewayId);
      }
      setIsGenerateConfirmationOpen(false);
    }
  };

  const closeGenerateConfirmationPopup = () => setIsGenerateConfirmationOpen(false);

  const submitGenerateIoTEdgeConnectionString = () => {
    const gatewayId = childGateway?.id;
    if (gatewayId) {
      generateSecrets(gatewayId);
    }
    setIsGenerateConfirmationOpen(false);
  };

  return {
    deviceId,
    ioTEdgeConnectionString,
    generateIoTEdgeConnectionStringIsLoading: generateSecretIsPending,
    isGenerateConfirmationOpen,
    defaultIoTEdgeConnectionString,

    generateIoTEdgeClusterDeviceConnectionString: generateIoTEdgeConnectionString,
    submitGenerateIoTEdgeClusterDeviceConnectionString: submitGenerateIoTEdgeConnectionString,
    closeGenerateConfirmationPopup,
  };
};

export default useGatewaySettingsIoTEdgeClusterClient;
